import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { TickerCommandDTO } from '../../../../../system/system-dtos';
import { ActionModalComponent } from '../../../../action/action-modal/action-modal.component';
import { TickerTableDataSource } from '../main-table-content-v2/TickerTableDataSource';
import { BlastV2Service } from '../../service/blast-v2.service';
import { LockService } from '../../service/lock-service';
import { AcceptWithdrawalComponent } from 'src/app/feature/manage/player/payments/withdraw-ticker/accept-withdrawal/accept-withdrawal.component';

@Component({
  selector: 'app-table-actions-bar-v2',
  templateUrl: './table-actions-bar-v2.component.html',
  styleUrls: ['./table-actions-bar-v2.component.scss'],
  standalone: false,
})
export class TableActionsBarV2Component implements OnChanges {
  _dataSource: TickerTableDataSource;

  @Input()
  set dataSource(ds: TickerTableDataSource) {
    if (ds) {
      this._dataSource = ds;
      this.subscribeTOPageDataChange();
    }
  }

  checkedPageEntries: any[];

  availableCommands: TickerCommandDTO[];

  private pageDataSubscription: Subscription;

  constructor(
    private blastV2Service: BlastV2Service,
    private lockService: LockService,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router
  ) {}

  private subscribeTOPageDataChange() {
    this.pageDataSubscription = this?._dataSource?.pageDataSubject.subscribe((pageData: any[]) =>
      this.onPageDataChange(pageData)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.dataSource) {
      return;
    }
    if (this.pageDataSubscription && !this.pageDataSubscription.closed) {
      this.pageDataSubscription.unsubscribe();
    }
    this.subscribeTOPageDataChange();
  }

  private onPageDataChange(pageData: any[]) {
    this.checkedPageEntries = pageData.filter((d) => d.checked);
    this.extractActions();
    this.removeResolveFromActions();
  }

  extractActions() {
    if (this.checkedPageEntries.length === 0) {
      this.availableCommands = [];
    }
    const commandForEntries = this.checkedPageEntries.map((entry) =>
      this.extractActionsForEntry(entry).map((c) => c.command)
    );

    this.availableCommands = this._dataSource.tableConfig.commands
      .filter((c) => c.actionsBar)
      .map((c) => c.command)
      .filter((command) => this.doAllEntriesApplyForCommand(commandForEntries, command))
      .map((commandName) =>
        this._dataSource.tableConfig.commands.find((commandDef) => commandDef.command === commandName)
      );
    this.changeDetectorRef.detectChanges();
  }

  removeResolveFromActions(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('withdraw')) {
      this.availableCommands = this.availableCommands.filter((item) => item.command !== 'withdrawticker.resolve');
    }
    this.changeDetectorRef.detectChanges();
  }

  private doAllEntriesApplyForCommand(commandForEntries: string[][], command: string) {
    return (
      commandForEntries.filter((groupedCommands) => groupedCommands.includes(command)).length ===
      commandForEntries.length
    );
  }

  extractActionsForEntry(entry: any): TickerCommandDTO[] {
    if (!this.lockService.isEntryLocked(entry)) {
      return [this._dataSource.tableConfig.commands.find((c) => c.command === 'lock')];
    }
    if (this.lockService.isEntryLockedByCurrentAdmin(entry)) {
      return this._dataSource.tableConfig.commands.filter((command) => {
        const data = entry;
        return command.jsEditCondition ? eval(command.jsEditCondition) : true;
      });
    }
    return [this._dataSource.tableConfig.commands.find((c) => c.command === 'unlock')];
  }

  private getCollectionName() {
    return `${this._dataSource.tableConfig.code}ticker`;
  }

  public executeCommand(command: string) {
    if (!this.checkedPageEntries || this.checkedPageEntries.length === 0) {
      return;
    }
    if (command === 'lock') {
      this.lockTickers();
      return;
    }
    const commandDef: TickerCommandDTO = this._dataSource.tableConfig.commands.find((c) => c.command === command);
    if (commandDef.confirmationRequired || (commandDef.rows && commandDef.rows.length > 0)) {
      this.executeCommandWithModal(command);
    } else {
      this.executeSimpleCommandWithNoModal(command);
    }
  }

  public executeCommandWithModal(command: string) {
    const commandDef: TickerCommandDTO = this._dataSource.tableConfig.commands.find((c) => c.command === command);

    let dialogRef: any;
    if (command === 'withdrawticker.confirm') {
      let foundObject = this._dataSource.data.find((obj) => obj.id === this.checkedPageEntries[0].id);
      dialogRef = this.dialog.open(AcceptWithdrawalComponent, {
        data: {
          checkedEntriesCount: this.checkedPageEntries.length,
          config: commandDef,
          playerId: foundObject.data.playerId,
          cardId: foundObject.data.cardId,
          paymentId: foundObject.data.paymentType.id,
          amount: foundObject.data.amount.value / 100,
        },
      });
    } else {
      dialogRef = this.dialog.open(ActionModalComponent, {
        minWidth: commandDef.minModalWidth ? commandDef.minModalWidth : undefined,
        data: {
          config: commandDef,
        },
      });
    }

    dialogRef
      .afterClosed()
      .pipe(filter((d) => !!d))
      .subscribe((data: any) => {
        this.checkedPageEntries.forEach((entry) => {
          this.doExecuteCommand(command, entry.id, Object.keys(data).length > 0 ? data : entry);
        });
      });
  }

  public executeSimpleCommandWithNoModal(command: string) {
    this.checkedPageEntries.forEach((entry) => {
      this.doExecuteCommand(command, entry.id, entry);
    });
  }

  public refresh() {
    this.doExecuteCommand('refresh', undefined, undefined);
  }

  public reload() {
    this.doExecuteCommand('reload', undefined, undefined);
  }

  private doExecuteCommand(command: string, id: number, element: any) {
    this.blastV2Service.sendMessage({
      cmd: command,
      data: element,
      tickerId: id,
      collection: this.getCollectionName(),
    });
  }

  lockTickers() {
    this.checkedPageEntries
      .filter((entry) => !this.lockService.isEntryLocked(entry))
      .forEach((entry) => {
        this.lockService.lockTicker(this.getCollectionName(), entry.id);
      });
  }
}
